import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ServiceContent = ({ service }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Eye' },
    { id: 'methodology', label: 'Methodology', icon: 'Settings' },
    { id: 'technology', label: 'Technology', icon: 'Code' },
    { id: 'case-studies', label: 'Case Studies', icon: 'FileText' }
  ];

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="space-y-8">
      {/* Tab Navigation */}
      <div className="border-b border-border">
        <nav className="flex space-x-8 overflow-x-auto">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap transition-colors duration-200 ${
                activeTab === tab?.id
                  ? 'border-accent text-accent' :'border-transparent text-text-secondary hover:text-foreground hover:border-muted'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </nav>
      </div>
      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-6"
      >
        {activeTab === 'overview' && (
          <div id="methodology" className="space-y-6">
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-4">Service Overview</h3>
              <p className="text-text-secondary leading-relaxed mb-6">
                {service?.description}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {service?.features?.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start space-x-3 p-4 bg-card rounded-lg border border-border"
                >
                  <div className="flex-shrink-0 w-8 h-8 bg-accent/20 rounded-lg flex items-center justify-center">
                    <Icon name={feature?.icon} size={16} className="text-accent" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">{feature?.title}</h4>
                    <p className="text-sm text-text-secondary">{feature?.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'methodology' && (
          <div id="methodology" className="space-y-6">
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-4">Our Methodology</h3>
              <p className="text-text-secondary leading-relaxed mb-6">
                We follow a proven, iterative approach that ensures successful project delivery and client satisfaction.
              </p>
            </div>

            <div className="space-y-4">
              {service?.methodology?.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start space-x-4 p-6 bg-card rounded-lg border border-border"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-accent rounded-lg flex items-center justify-center text-accent-foreground font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">{step?.title}</h4>
                    <p className="text-text-secondary">{step?.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'technology' && (
          <div id="technology" className="space-y-6">
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-4">Technology Stack</h3>
              <p className="text-text-secondary leading-relaxed mb-6">
                We use modern technologies and industry best practices to deliver robust, scalable solutions.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {service?.technologies?.map((tech, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="p-4 bg-card rounded-lg border border-border text-center hover:border-accent/50 transition-colors duration-200"
                >
                  <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Icon name={tech?.icon} size={20} className="text-accent" />
                  </div>
                  <h4 className="font-semibold text-foreground mb-1">{tech?.name}</h4>
                  <p className="text-sm text-text-secondary">{tech?.category}</p>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'case-studies' && (
          <div id="case-studies" className="space-y-6">
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-4">Success Stories</h3>
              <p className="text-text-secondary leading-relaxed mb-6">
                Explore how we've helped businesses transform their operations and achieve their goals.
              </p>
            </div>

            <div className="space-y-6">
              {service?.caseStudies?.map((study, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="p-6 bg-card rounded-lg border border-border"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-semibold text-foreground mb-2">{study?.title}</h4>
                      <p className="text-accent font-medium">{study?.client}</p>
                    </div>
                    <span className="px-3 py-1 bg-success/20 text-success rounded-full text-sm font-medium">
                      {study?.result}
                    </span>
                  </div>
                  <p className="text-text-secondary mb-4">{study?.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {study?.technologies?.map((tech, techIndex) => (
                      <span 
                        key={techIndex}
                        className="px-2 py-1 bg-muted text-muted-foreground rounded text-xs"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </motion.div>
      {/* FAQ Section */}
      <div className="mt-12 pt-8 border-t border-border">
        <h3 className="text-2xl font-bold text-foreground mb-6">Frequently Asked Questions</h3>
        <div className="space-y-4">
          {service?.faqs?.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="border border-border rounded-lg overflow-hidden"
            >
              <button
                onClick={() => toggleFaq(index)}
                className="w-full px-6 py-4 text-left flex items-center justify-between bg-card hover:bg-muted/50 transition-colors duration-200"
              >
                <span className="font-medium text-foreground">{faq?.question}</span>
                <Icon 
                  name={expandedFaq === index ? "ChevronUp" : "ChevronDown"} 
                  size={20} 
                  className="text-text-secondary" 
                />
              </button>
              {expandedFaq === index && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="px-6 py-4 bg-muted/30 border-t border-border"
                >
                  <p className="text-text-secondary">{faq?.answer}</p>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ServiceContent;